<template>
  <div class="plot-add-modal__backdrop" @click="onBackdrop">
    <div class="plot-add-modal" @click.stop>
      <!-- Modo: alta manual de una nave -->
      <template v-if="mode === 'create'">
        <h4>Registrar primera nave</h4>
        <p class="plot-add-modal__hint">Captura los datos mínimos. Podrás editar la nave más adelante.</p>

        <label class="plot-add-modal__label">Código de nave *</label>
        <input v-model="form.name" class="plot-add-modal__input" placeholder="Ej. Nave A1" />

        <label class="plot-add-modal__label">Zona</label>
        <input v-model="form.internalLocation" class="plot-add-modal__input" placeholder="Ej. Zona norte" />

        <label class="plot-add-modal__label">Metros cuadrados</label>
        <input v-model="form.areaM2" type="number" min="0" class="plot-add-modal__input" placeholder="Ej. 850" />

        <p v-if="errorMsg" class="plot-add-modal__error">{{ errorMsg }}</p>

        <div class="plot-add-modal__actions">
          <button class="plot-add-modal__btn plot-add-modal__btn--secondary" @click="$emit('close')">Cancelar</button>
          <button class="plot-add-modal__btn" :disabled="saving" @click="submitCreate">
            {{ saving ? 'Guardando...' : 'Registrar nave' }}
          </button>
        </div>
      </template>

      <!-- Modo: importar desde archivo -->
      <template v-else-if="step === 'file'">
        <h4>Importar naves desde archivo</h4>
        <p class="plot-add-modal__hint">
          Sube un archivo CSV con las columnas <strong>nombre</strong>, <strong>zona</strong> (opcional) y
          <strong>area_m2</strong> (opcional). Si la primera fila trae los títulos, se leen por nombre; si no, se toman en ese orden: nombre, zona y m².
        </p>

        <label
          class="plot-add-modal__dropzone"
          :class="{ 'plot-add-modal__dropzone--active': dragActive }"
          @dragover.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="onDrop"
        >
          <span class="mdi mdi-tray-arrow-up plot-add-modal__dropzone-icon"></span>
          <span v-if="!selectedFile">Arrastra tu archivo CSV aquí o haz clic para seleccionarlo</span>
          <span v-else>{{ selectedFile.name }} ({{ parsedRows.length }} filas detectadas)</span>
          <input ref="fileInput" type="file" accept=".csv" class="plot-add-modal__file-input" @change="onFileChange" />
        </label>

        <p v-if="errorMsg" class="plot-add-modal__error">{{ errorMsg }}</p>

        <div class="plot-add-modal__actions">
          <button class="plot-add-modal__btn plot-add-modal__btn--secondary" @click="$emit('close')">Cancelar</button>
          <button class="plot-add-modal__btn" :disabled="saving || parsedRows.length === 0" @click="submitImport">
            {{ saving ? 'Revisando...' : `Importar ${parsedRows.length || ''} naves` }}
          </button>
        </div>
      </template>

      <!-- Revisión de nombres repetidos -->
      <template v-else-if="step === 'review'">
        <h4>Naves con nombre repetido</h4>
        <p class="plot-add-modal__hint">
          {{ dupItems.length }} {{ dupItems.length === 1 ? 'nave ya existe' : 'naves ya existen' }} en el parque o aparecen más de una vez en el archivo.
          Elige qué hacer con cada una. Las demás ({{ parsedRows.length - dupItems.length }}) se importan normalmente.
        </p>

        <div class="plot-add-modal__dups">
          <div v-for="d in dupItems" :key="d.index" class="plot-add-modal__dup">
            <div class="plot-add-modal__dup-head">
              <strong>{{ d.name }}</strong>
              <span class="plot-add-modal__dup-reason">{{ d.reason === 'EXISTING' ? 'Ya existe en el parque' : 'Repetida en el archivo' }}</span>
            </div>
            <div class="plot-add-modal__dup-actions">
              <select v-model="d.action" class="plot-add-modal__input plot-add-modal__select">
                <option value="skip">Omitir</option>
                <option value="rename">Cambiar nombre</option>
                <option value="keep">Crear duplicada</option>
              </select>
              <input
                v-if="d.action === 'rename'"
                v-model="d.newName"
                class="plot-add-modal__input"
                placeholder="Nuevo nombre"
              />
            </div>
          </div>
        </div>

        <p v-if="errorMsg" class="plot-add-modal__error">{{ errorMsg }}</p>

        <div class="plot-add-modal__actions">
          <button class="plot-add-modal__btn plot-add-modal__btn--secondary" :disabled="saving" @click="backToFile">Volver</button>
          <button class="plot-add-modal__btn" :disabled="saving" @click="confirmReview">
            {{ saving ? 'Importando...' : 'Continuar importación' }}
          </button>
        </div>
      </template>

      <!-- Resultado -->
      <template v-else>
        <h4>Importación terminada</h4>
        <p class="plot-add-modal__hint">
          Se crearon <strong>{{ result.created }}</strong> {{ result.created === 1 ? 'nave' : 'naves' }}.
          <template v-if="result.skippedNames.length">
            Se omitieron {{ result.skippedNames.length }} por nombre repetido: {{ result.skippedNames.join(', ') }}.
          </template>
        </p>
        <div class="plot-add-modal__actions">
          <button class="plot-add-modal__btn" @click="$emit('created')">Listo</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { createPlot, createPlotsBulk } from '@/services/plots'

const props = defineProps({
  parkId: { type: String, required: true },
  mode: { type: String, default: 'create' }, // 'create' | 'import'
})

const emit = defineEmits(['close', 'created'])

const saving = ref(false)
const errorMsg = ref('')

// --- alta manual ---
const form = ref({ name: '', internalLocation: '', areaM2: '' })

async function submitCreate() {
  errorMsg.value = ''
  if (!form.value.name.trim()) {
    errorMsg.value = 'El código de nave es obligatorio.'
    return
  }
  saving.value = true
  try {
    await createPlot(props.parkId, {
      name: form.value.name.trim(),
      internalLocation: form.value.internalLocation.trim() || null,
      areaM2: form.value.areaM2 || null,
    })
    emit('created')
  } catch (e) {
    errorMsg.value = e.code === 'PLAN_LIMIT_PLOTS' || e.code === 'DUPLICATE_PLOT_NAME' ? e.message : 'No se pudo registrar la nave. Intenta de nuevo.'
  } finally {
    saving.value = false
  }
}

// --- importar desde archivo ---
const fileInput = ref(null)
const dragActive = ref(false)
const selectedFile = ref(null)
const parsedRows = ref([])

// Con encabezados (nombre, zona, area_m2) en cualquier orden; sin encabezados, por posición: nombre, zona, m².
function parseCsv(text) {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim())
  if (!lines.length) return []
  const sep = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ','
  const split = (l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, ''))
  const headers = split(lines[0]).map((h) => h.toLowerCase())
  const nameIdx = headers.findIndex((h) => ['nombre', 'name', 'codigo', 'código', 'nave'].includes(h))
  const zoneIdx = headers.findIndex((h) => ['zona', 'zone', 'internallocation'].includes(h))
  const areaIdx = headers.findIndex((h) => ['area_m2', 'áream2', 'aream2', 'areamt2', 'area', 'área', 'm2', 'm²', 'superficie'].includes(h))
  const hasHeader = nameIdx >= 0 || zoneIdx >= 0 || areaIdx >= 0
  const num = (v) => (v ? Number(String(v).replace(/[^0-9.]/g, '')) || undefined : undefined)
  const isNumber = (v) => /^[0-9]+([.,][0-9]+)?$/.test(String(v || '').trim())

  return (hasHeader ? lines.slice(1) : lines)
    .map((line) => {
      const cols = split(line)
      if (hasHeader) {
        return {
          name: nameIdx >= 0 ? cols[nameIdx] : cols[0],
          internalLocation: zoneIdx >= 0 ? cols[zoneIdx] || undefined : undefined,
          areaM2: areaIdx >= 0 ? num(cols[areaIdx]) : undefined,
        }
      }
      if (cols.length >= 3) return { name: cols[0], internalLocation: cols[1] || undefined, areaM2: num(cols[2]) }
      if (cols.length === 2) return isNumber(cols[1]) ? { name: cols[0], areaM2: num(cols[1]) } : { name: cols[0], internalLocation: cols[1] || undefined }
      return { name: cols[0] }
    })
    .filter((r) => r.name)
}

function handleFile(file) {
  errorMsg.value = ''
  selectedFile.value = file
  const reader = new FileReader()
  reader.onload = () => {
    const rows = parseCsv(String(reader.result || ''))
    if (rows.length === 0) {
      errorMsg.value = 'No se encontraron filas válidas en el archivo.'
    }
    parsedRows.value = rows
  }
  reader.readAsText(file)
}

function onDrop(e) {
  dragActive.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) handleFile(file)
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (file) handleFile(file)
}

// Paso 1: se revisa el archivo contra las naves existentes (sin crear nada). Si hay nombres repetidos se pregunta qué hacer.
const step = ref('file') // 'file' | 'review' | 'done'
const dupItems = ref([])
const result = ref({ created: 0, skippedNames: [] })

async function submitImport() {
  errorMsg.value = ''
  saving.value = true
  try {
    const check = await createPlotsBulk(props.parkId, parsedRows.value, { dryRun: true })
    const pending = (check.duplicates || []).filter((d) => !parsedRows.value[d.index]?.allowDuplicate)
    if (pending.length) {
      openReview(pending)
    } else {
      await runImport(parsedRows.value)
    }
  } catch (e) {
    errorMsg.value = importError(e)
  } finally {
    saving.value = false
  }
}

function openReview(pending) {
  dupItems.value = pending.map((d) => ({ ...d, action: 'skip', newName: `${d.name}-2` }))
  step.value = 'review'
}

function backToFile() {
  errorMsg.value = ''
  step.value = 'file'
}

// Paso 2: se aplica la decisión de cada fila repetida y se vuelve a revisar (un nombre nuevo también podría chocar).
async function confirmReview() {
  errorMsg.value = ''
  const byIndex = new Map(dupItems.value.map((d) => [d.index, d]))
  const rows = []
  for (let i = 0; i < parsedRows.value.length; i++) {
    const row = parsedRows.value[i]
    const d = byIndex.get(i)
    if (!d) { rows.push(row); continue }
    if (d.action === 'skip') continue
    if (d.action === 'keep') { rows.push({ ...row, allowDuplicate: true }); continue }
    const newName = d.newName.trim()
    if (!newName) { errorMsg.value = `Escribe el nuevo nombre para "${d.name}" o elige otra opción.`; return }
    rows.push({ ...row, name: newName })
  }
  if (!rows.length) { errorMsg.value = 'No queda ninguna nave por importar.'; return }

  saving.value = true
  try {
    const check = await createPlotsBulk(props.parkId, rows, { dryRun: true })
    const pending = (check.duplicates || []).filter((d) => !rows[d.index]?.allowDuplicate)
    if (pending.length) {
      parsedRows.value = rows
      openReview(pending)
      errorMsg.value = 'Algunos nombres nuevos también están repetidos. Revisa de nuevo.'
    } else {
      await runImport(rows)
    }
  } catch (e) {
    errorMsg.value = importError(e)
  } finally {
    saving.value = false
  }
}

async function runImport(rows) {
  const res = await createPlotsBulk(props.parkId, rows)
  result.value = { created: res.created || 0, skippedNames: res.skippedNames || [] }
  step.value = 'done'
}

function importError(e) {
  return e.code === 'PLAN_LIMIT_PLOTS' ? e.message : 'No se pudo importar el archivo. Verifica el formato e intenta de nuevo.'
}

function onBackdrop() {
  // Si ya se crearon naves, al cerrar se refresca el panel igual que con "Listo".
  if (step.value === 'done') emit('created')
  else emit('close')
}
</script>

<style scoped>
.plot-add-modal__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.plot-add-modal {
  background: #ffffff;
  border-radius: 10px;
  padding: 1.4rem;
  min-width: 360px;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.plot-add-modal h4 {
  margin: 0 0 0.15rem;
  font-family: var(--font-display);
  font-size: 15px;
  color: #1e3a5f;
}

.plot-add-modal__hint {
  margin: 0 0 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.plot-add-modal__label {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  margin-top: 0.3rem;
}

.plot-add-modal__input {
  font-family: var(--font-body);
  font-size: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  width: 100%;
  box-sizing: border-box;
}

.plot-add-modal__input:focus {
  outline: none;
  border-color: #3b82f6;
}

.plot-add-modal__error {
  margin: 0.3rem 0 0;
  font-family: var(--font-body);
  font-size: 12px;
  color: #dc2626;
}

.plot-add-modal__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.8rem;
}

.plot-add-modal__btn {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 0.9rem;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.plot-add-modal__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.plot-add-modal__btn--secondary {
  background: #ffffff;
  color: var(--text-content);
  border: 1px solid #e2e8f0;
}

.plot-add-modal__dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  text-align: center;
  border: 1.5px dashed #cbd5e1;
  border-radius: 8px;
  padding: 1.6rem 1rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.plot-add-modal__dropzone--active {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.plot-add-modal__dropzone-icon {
  font-size: 22px;
  color: #94a3b8;
}

.plot-add-modal__dups { display: flex; flex-direction: column; gap: 0.5rem; max-height: 320px; overflow-y: auto; padding-right: 2px; }
.plot-add-modal__dup { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.55rem 0.65rem; display: flex; flex-direction: column; gap: 0.4rem; }
.plot-add-modal__dup-head { display: flex; justify-content: space-between; gap: 0.5rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.plot-add-modal__dup-reason { color: #b45309; font-size: 11px; }
.plot-add-modal__dup-actions { display: flex; gap: 0.4rem; }
.plot-add-modal__select { width: auto; flex-shrink: 0; }

.plot-add-modal__file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
</style>